const FRAME_CAPACITY = 2_048;

export interface FramePerformanceSample {
  rawFrameMs: number;
  cpuFrameMs: number;
  fixedPhysicsMs: number;
  simulationMs: number;
  animateCityMs: number;
  wallUpdateMs: number;
  vehicleFrameMs: number;
  systemFrameMs: number;
  lightingFrameMs: number;
  skyFrameMs: number;
  renderMs: number;
  fixedSteps: number;
  simulationSteps: number;
  rendererCalls: number;
  triangles: number;
  droppedDebtMs: number;
}

export interface StreamingPerformanceSample {
  fetchMs: number;
  decodeMs: number;
  buildSliceMs: number;
  commitMs: number;
  disposeSliceMs: number;
  committed: boolean;
  cancelled: boolean;
  generation: number;
  pendingLoads: number;
  preparedTiles: number;
  retiredTiles: number;
  activeMapTiles: number;
}

export interface PerformanceDistribution {
  p50: number;
  p95: number;
  p99: number;
  max: number;
}

export type FramePhaseName =
  | "rawFrameMs"
  | "cpuFrameMs"
  | "fixedPhysicsMs"
  | "simulationMs"
  | "animateCityMs"
  | "wallUpdateMs"
  | "vehicleFrameMs"
  | "systemFrameMs"
  | "lightingFrameMs"
  | "skyFrameMs"
  | "renderMs";

export interface PerformanceSnapshot {
  sampleCount: number;
  phases: Record<FramePhaseName, PerformanceDistribution>;
  framesOver16_7Ms: number;
  framesOver33_3Ms: number;
  framesOver50Ms: number;
  maxFixedSteps: number;
  maxSimulationSteps: number;
  totalDroppedDebtMs: number;
  latestRendererCalls: number;
  latestTriangles: number;
  streaming: {
    loads: number;
    commits: number;
    cancellations: number;
    maxFetchMs: number;
    maxDecodeMs: number;
    maxBuildSliceMs: number;
    maxCommitMs: number;
    maxDisposeSliceMs: number;
    generation: number;
    pendingLoads: number;
    preparedTiles: number;
    retiredTiles: number;
    activeMapTiles: number;
  };
}

export interface FixedDebtAdvance {
  steps: number;
  remainderSeconds: number;
  droppedSeconds: number;
}

const FRAME_PHASES = [
  "rawFrameMs",
  "cpuFrameMs",
  "fixedPhysicsMs",
  "simulationMs",
  "animateCityMs",
  "wallUpdateMs",
  "vehicleFrameMs",
  "systemFrameMs",
  "lightingFrameMs",
  "skyFrameMs",
  "renderMs",
] as const satisfies readonly FramePhaseName[];

export function advanceFixedDebt(
  debtSeconds: number,
  stepSeconds: number,
  maxSteps: number,
): FixedDebtAdvance {
  if (!Number.isFinite(debtSeconds) || debtSeconds < 0)
    throw new RangeError("Debt must be finite and non-negative");
  if (!Number.isFinite(stepSeconds) || stepSeconds <= 0)
    throw new RangeError("Step must be finite and positive");
  if (!Number.isInteger(maxSteps) || maxSteps < 0)
    throw new RangeError("Maximum steps must be a non-negative integer");
  const availableSteps = Math.floor(debtSeconds / stepSeconds);
  const steps = Math.min(availableSteps, maxSteps);
  const remainderSeconds = debtSeconds - availableSteps * stepSeconds;
  return {
    steps,
    remainderSeconds,
    droppedSeconds: Math.max(0, availableSteps - steps) * stepSeconds,
  };
}

function emptyDistribution(): PerformanceDistribution {
  return { p50: 0, p95: 0, p99: 0, max: 0 };
}

function percentile(sorted: readonly number[], fraction: number): number {
  if (sorted.length === 0) return 0;
  return sorted[Math.ceil(sorted.length * fraction) - 1] ?? 0;
}

export class PerformanceMonitor {
  readonly #rings: Record<FramePhaseName, Float32Array> = {
    rawFrameMs: new Float32Array(FRAME_CAPACITY),
    cpuFrameMs: new Float32Array(FRAME_CAPACITY),
    fixedPhysicsMs: new Float32Array(FRAME_CAPACITY),
    simulationMs: new Float32Array(FRAME_CAPACITY),
    animateCityMs: new Float32Array(FRAME_CAPACITY),
    wallUpdateMs: new Float32Array(FRAME_CAPACITY),
    vehicleFrameMs: new Float32Array(FRAME_CAPACITY),
    systemFrameMs: new Float32Array(FRAME_CAPACITY),
    lightingFrameMs: new Float32Array(FRAME_CAPACITY),
    skyFrameMs: new Float32Array(FRAME_CAPACITY),
    renderMs: new Float32Array(FRAME_CAPACITY),
  };
  #writeIndex = 0;
  #sampleCount = 0;
  #framesOver16_7Ms = 0;
  #framesOver33_3Ms = 0;
  #framesOver50Ms = 0;
  #maxFixedSteps = 0;
  #maxSimulationSteps = 0;
  #totalDroppedDebtMs = 0;
  #latestRendererCalls = 0;
  #latestTriangles = 0;
  #loads = 0;
  #commits = 0;
  #cancellations = 0;
  #maxFetchMs = 0;
  #maxDecodeMs = 0;
  #maxBuildSliceMs = 0;
  #maxCommitMs = 0;
  #maxDisposeSliceMs = 0;
  #generation = 0;
  #pendingLoads = 0;
  #preparedTiles = 0;
  #retiredTiles = 0;
  #activeMapTiles = 0;

  reset(): void {
    this.#writeIndex = 0;
    this.#sampleCount = 0;
    this.#framesOver16_7Ms = 0;
    this.#framesOver33_3Ms = 0;
    this.#framesOver50Ms = 0;
    this.#maxFixedSteps = 0;
    this.#maxSimulationSteps = 0;
    this.#totalDroppedDebtMs = 0;
    this.#latestRendererCalls = 0;
    this.#latestTriangles = 0;
    this.#loads = 0;
    this.#commits = 0;
    this.#cancellations = 0;
    this.#maxFetchMs = 0;
    this.#maxDecodeMs = 0;
    this.#maxBuildSliceMs = 0;
    this.#maxCommitMs = 0;
    this.#maxDisposeSliceMs = 0;
  }

  recordFrame(sample: FramePerformanceSample): void {
    for (const phase of FRAME_PHASES) this.#rings[phase][this.#writeIndex] = sample[phase];
    this.#writeIndex = (this.#writeIndex + 1) % FRAME_CAPACITY;
    this.#sampleCount = Math.min(this.#sampleCount + 1, FRAME_CAPACITY);
    if (sample.rawFrameMs > 16.7) this.#framesOver16_7Ms += 1;
    if (sample.rawFrameMs > 33.3) this.#framesOver33_3Ms += 1;
    if (sample.rawFrameMs > 50) this.#framesOver50Ms += 1;
    this.#maxFixedSteps = Math.max(this.#maxFixedSteps, sample.fixedSteps);
    this.#maxSimulationSteps = Math.max(this.#maxSimulationSteps, sample.simulationSteps);
    this.#totalDroppedDebtMs += sample.droppedDebtMs;
    this.#latestRendererCalls = sample.rendererCalls;
    this.#latestTriangles = sample.triangles;
  }

  recordStreaming(sample: StreamingPerformanceSample): void {
    if (sample.fetchMs > 0 || sample.decodeMs > 0) this.#loads += 1;
    if (sample.committed) this.#commits += 1;
    if (sample.cancelled) this.#cancellations += 1;
    this.#maxFetchMs = Math.max(this.#maxFetchMs, sample.fetchMs);
    this.#maxDecodeMs = Math.max(this.#maxDecodeMs, sample.decodeMs);
    this.#maxBuildSliceMs = Math.max(this.#maxBuildSliceMs, sample.buildSliceMs);
    this.#maxCommitMs = Math.max(this.#maxCommitMs, sample.commitMs);
    this.#maxDisposeSliceMs = Math.max(this.#maxDisposeSliceMs, sample.disposeSliceMs);
    this.#generation = sample.generation;
    this.#pendingLoads = sample.pendingLoads;
    this.#preparedTiles = sample.preparedTiles;
    this.#retiredTiles = sample.retiredTiles;
    this.#activeMapTiles = sample.activeMapTiles;
  }

  snapshot(): PerformanceSnapshot {
    const phases = {} as Record<FramePhaseName, PerformanceDistribution>;
    for (const phase of FRAME_PHASES) {
      if (this.#sampleCount === 0) {
        phases[phase] = emptyDistribution();
        continue;
      }
      const values = Array.from(this.#rings[phase].subarray(0, this.#sampleCount)).sort(
        (left, right) => left - right,
      );
      phases[phase] = {
        p50: percentile(values, 0.5),
        p95: percentile(values, 0.95),
        p99: percentile(values, 0.99),
        max: values[values.length - 1] ?? 0,
      };
    }
    return {
      sampleCount: this.#sampleCount,
      phases,
      framesOver16_7Ms: this.#framesOver16_7Ms,
      framesOver33_3Ms: this.#framesOver33_3Ms,
      framesOver50Ms: this.#framesOver50Ms,
      maxFixedSteps: this.#maxFixedSteps,
      maxSimulationSteps: this.#maxSimulationSteps,
      totalDroppedDebtMs: this.#totalDroppedDebtMs,
      latestRendererCalls: this.#latestRendererCalls,
      latestTriangles: this.#latestTriangles,
      streaming: {
        loads: this.#loads,
        commits: this.#commits,
        cancellations: this.#cancellations,
        maxFetchMs: this.#maxFetchMs,
        maxDecodeMs: this.#maxDecodeMs,
        maxBuildSliceMs: this.#maxBuildSliceMs,
        maxCommitMs: this.#maxCommitMs,
        maxDisposeSliceMs: this.#maxDisposeSliceMs,
        generation: this.#generation,
        pendingLoads: this.#pendingLoads,
        preparedTiles: this.#preparedTiles,
        retiredTiles: this.#retiredTiles,
        activeMapTiles: this.#activeMapTiles,
      },
    };
  }
}
